import { useState, useCallback, useEffect } from 'react';
import {
  getChildren,
  addChild,
  updateChild,
  deleteChild,
  getSurveys,
  getSurvey,
  saveSurveyAnswer,
  completeSurvey,
  Child,
  Survey,
} from '../services/childrenSurveysService';

export const useChildrenSurveys = () => {
  const [children, setChildren] = useState<Child[]>([]);
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState(false);
  const [isLoadingSurveys, setIsLoadingSurveys] = useState(false);
  const [childrenError, setChildrenError] = useState<string | null>(null);
  const [surveysError, setSurveysError] = useState<string | null>(null);

  // Fetch children on mount
  useEffect(() => {
    const loadChildren = async () => {
      setIsLoadingChildren(true);
      try {
        const data = await getChildren();
        setChildren(data.children);
      } catch (err: any) {
        setChildrenError(err.message || 'Error loading children');
      } finally {
        setIsLoadingChildren(false);
      }
    };
    loadChildren();
  }, []);

  // Fetch surveys on mount
  useEffect(() => {
    const loadSurveys = async () => {
      setIsLoadingSurveys(true);
      try {
        const data = await getSurveys();
        setSurveys(data.surveys);
      } catch (err: any) {
        setSurveysError(err.message || 'Error loading surveys');
      } finally {
        setIsLoadingSurveys(false);
      }
    };
    loadSurveys();
  }, []);

  // Add child
  const handleAddChild = useCallback(
    async (data: Omit<Child, 'id' | 'createdAt'>) => {
      try {
        const result = await addChild(data);
        if (result.child) {
          setChildren([...children, result.child]);
        }
        return { success: true, data: result.child };
      } catch (error) {
        return { success: false, error };
      }
    },
    [children]
  );

  // Update child
  const handleUpdateChild = useCallback(
    async (id: string, data: Partial<Child>) => {
      try {
        const result = await updateChild(id, data);
        setChildren(children.map((c) => (c.id === id ? result.child : c)));
        return { success: true };
      } catch (error) {
        return { success: false, error };
      }
    },
    [children]
  );

  // Delete child
  const handleDeleteChild = useCallback(
    async (id: string) => {
      try {
        await deleteChild(id);
        setChildren(children.filter((c) => c.id !== id));
        return { success: true };
      } catch (error) {
        return { success: false, error };
      }
    },
    [children]
  );

  // Complete survey
  const handleCompleteSurvey = useCallback(
    async (id: string) => {
      try {
        await completeSurvey(id);
        setSurveys(surveys.map((s) => (s.id === id ? { ...s, completed: true } : s)));
        return { success: true };
      } catch (error) {
        return { success: false, error };
      }
    },
    [surveys]
  );

  return {
    // Data
    children,
    surveys,

    // Loading states
    isLoadingChildren,
    isLoadingSurveys,

    // Error states
    childrenError,
    surveysError,

    // Actions
    handleAddChild,
    handleUpdateChild,
    handleDeleteChild,
    handleCompleteSurvey,
    getSurvey,
    saveSurveyAnswer,
  };
};
